import React, { useState } from 'react';

import Garage from '../../media/images/modules/parking-garage.jpg';
import { ScreenHead } from '../../ui/screen-parts.js';
import { Chip, Field, Kpis, Panel, Empty, useFlash } from '../../ui/kit.js';
import { day, clock, nextId } from '../../state/building.js';

const LEVELS = [
    { name: 'P1', note: 'Resident, valet stack', used: 88, total: 96 },
    { name: 'P2', note: 'Resident assigned', used: 101, total: 120 },
    { name: 'P3', note: 'Resident assigned, EV row', used: 74, total: 110 },
    { name: 'Guest', note: 'Ground level, 4 hour max', used: 17, total: 20 }
];

const PERMITS = [
    { id: 1, plate: 'FL JDR-2201', holder: 'Alex Rivera, 1204', kind: 'Resident', space: 'P2-114', expires: 'Dec 31', tone: 'ok', status: 'Valid' },
    { id: 2, plate: 'FL MCS-8803', holder: 'Mariana Costa, 803', kind: 'Resident', space: 'P1-022', expires: 'Dec 31', tone: 'ok', status: 'Valid' },
    { id: 3, plate: 'FL KXR-4471', holder: 'Guest of 1502', kind: 'Guest pass', space: 'Guest', expires: `${day(0)}, 6:00 PM`, tone: 'warn', status: 'Expires today' },
    { id: 4, plate: 'GA 7TQ-119', holder: 'Jordan Blake, guest of 1204', kind: 'Guest pass', space: 'Guest', expires: day(1), tone: 'ok', status: 'Valid' },
    { id: 5, plate: 'FL APX-3090', holder: 'Apex Plumbing', kind: 'Contractor', space: 'Loading dock', expires: day(-1), tone: 'alert', status: 'Expired' },
    { id: 6, plate: 'FL BLE-5520', holder: 'Brightline Electric', kind: 'Contractor', space: 'P3 service bay', expires: day(4), tone: 'ok', status: 'Valid' },
    { id: 7, plate: 'NY HWK-6612', holder: 'Daniel Weiss, 1502', kind: 'Resident', space: 'P3-061', expires: day(9), tone: 'warn', status: 'Renew soon' }
];

const VIOLATIONS = [
    { id: 1, plate: 'FL APX-3090', where: 'Loading dock', issue: 'Contractor permit expired yesterday', at: `${day(0)}, 8:20 AM` },
    { id: 2, plate: 'FL TNZ-0457', where: 'P2-118 (assigned to 1110)', issue: 'Parked in an assigned space, no permit', at: `${day(0)}, 6:55 AM` },
    { id: 3, plate: 'FL QRL-7734', where: 'Guest G-07', issue: 'Over 4 hour guest limit', at: `${day(-1)}, 11:40 PM` }
];

const normalize = plate => plate.toUpperCase().replace(/[^A-Z0-9]/g, '');

/* Parking Management: garage occupancy by level, every permit on file, a
   plate lookup for the guard on patrol, and the violations queue. */
function ParkingScreen() {
    const [violations, setViolations] = useState(VIOLATIONS);
    const [plate, setPlate] = useState('');
    const [found, setFound] = useState(null);
    const [flash, notify] = useFlash();

    const lookup = event => {
        event.preventDefault();
        const wanted = normalize(plate);
        if (!wanted) return notify('Type a plate to look up.');

        const permit = PERMITS.find(p => normalize(p.plate).endsWith(wanted) || normalize(p.plate) === wanted);
        setFound(permit || null);

        if (permit) {
            notify(`${permit.plate}: ${permit.kind} permit for ${permit.holder}, ${permit.status.toLowerCase()}.`);
        } else {
            setViolations(v => [{ id: nextId(), plate: plate.toUpperCase(), where: 'Reported on patrol', issue: 'No permit on file, tow review', at: `${day(0)}, ${clock()}` }, ...v]);
            notify('No permit on file, flag for tow review. Added to the violations queue.');
        }
    };

    const resolve = v => {
        setViolations(list => list.filter(item => item.id !== v.id));
        notify(`${v.plate} resolved and removed from the queue.`);
    };

    const used = LEVELS.reduce((sum, l) => sum + l.used, 0);
    const total = LEVELS.reduce((sum, l) => sum + l.total, 0);

    return (
        <div className="screen-content">
            <ScreenHead eyebrow="Parking management" title="Parking & garage" />

            <div className="banner">
                <img src={Garage} alt="" />
                <p className="banner__caption">Assigned spaces, guest passes, and valet requests for the garage.</p>
            </div>

            <Kpis items={[
                { label: 'Garage occupancy', value: `${Math.round((used / total) * 100)}%`, note: `${used} of ${total} spaces` },
                { label: 'Guest spaces free', value: LEVELS[3].total - LEVELS[3].used, tone: 'warn' },
                { label: 'Active permits', value: PERMITS.filter(p => p.status !== 'Expired').length, tone: 'ok' },
                { label: 'Open violations', value: violations.length, tone: violations.length ? 'alert' : 'ok' }
            ]} />

            {flash}

            <div className="cards">
                {LEVELS.map((level, i) => {
                    const pct = Math.round((level.used / level.total) * 100);
                    const tone = pct >= 90 ? ' meter--alert' : pct >= 80 ? ' meter--warn' : '';

                    return (
                        <Panel key={level.name} index={i}>
                            <div className="row__end" style={{ justifyContent: 'space-between' }}>
                                <h4>{level.name}</h4>
                                <Chip tone={pct >= 90 ? 'alert' : pct >= 80 ? 'warn' : 'ok'}>{pct}% full</Chip>
                            </div>
                            <div className={`meter${tone}`}>
                                <span className="meter__fill" style={{ width: `${pct}%` }} />
                            </div>
                            <p className="row__meta">{level.used} of {level.total} spaces · {level.note}</p>
                        </Panel>
                    );
                })}
            </div>

            <div className="split section-gap">
                <div className="stack">
                    <Panel as="form" className="panel--accent" onSubmit={lookup}>
                        <h4>Plate lookup</h4>
                        <div className="form">
                            <Field label="Plate number" wide>
                                <input
                                    className="form-control"
                                    value={plate}
                                    onChange={e => setPlate(e.target.value)}
                                    placeholder="e.g. KXR-4471"
                                />
                            </Field>
                            <button type="submit" className="btn btn-primary">Look up</button>
                        </div>
                        {found && (
                            <p className="row__meta">
                                {found.plate} · {found.holder} · {found.space} · expires {found.expires}
                            </p>
                        )}
                    </Panel>

                    <h4>Violations</h4>
                    {violations.length === 0 ? <Empty>No open violations. The garage is clear.</Empty> : (
                        <ul className="rows">
                            {violations.map(v => (
                                <li className="row" key={v.id}>
                                    <div className="row__main">
                                        <span className="row__title">{v.plate}</span>
                                        <span className="row__meta">{v.issue} · {v.where} · {v.at}</span>
                                    </div>
                                    <div className="row__end">
                                        <button type="button" className="btn btn-outline-light btn-sm" onClick={() => resolve(v)}>Resolve</button>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                <div className="stack">
                    <h4>Permits on file</h4>
                    <ul className="rows">
                        {PERMITS.map(p => (
                            <li className="row" key={p.id}>
                                <div className="row__main">
                                    <span className="row__title">{p.plate}</span>
                                    <span className="row__meta">{p.holder} · {p.kind} · {p.space} · expires {p.expires}</span>
                                </div>
                                <div className="row__end">
                                    <Chip tone={p.tone}>{p.status}</Chip>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </div>
    );
}

export default ParkingScreen;
